import { getColumnId, getColumnsWithPinnedProp, useTableHideAndPinSettings } from 'components/TableSettings';
import type { BimActKc2ListItemData } from 'gql/graphql';
import { BimActKc2GroupTypeEnum } from 'gql/graphql';
import { useCommonActsKC2GroupDelete } from 'pages/Project/Common/ActsKC2ListContainer/hooks';

import type { ColDef } from 'ag-grid-community';
import { useCallback, useMemo, useState } from 'react';

import type { IActKc2OtherItem } from '../../BimWorkOrderActsKc2List';
import { bimRemoveWorkOrderActsKC2GroupGql } from '../BimWorkOrderActsKc2ListQueries';

import { useBimActsKc2ListColumns } from './useBimActsKc2ListColumns';
import { useGetBimActsKc2List } from './useGetBimActsKc2List';

export const useBimActsKc2ListData = ({
    workOrderId,
    activeGroupTypeId,
    onOpenOtherActKc2Details,
    onOpenActsSmrDetail,
}: {
    workOrderId: number;
    activeGroupTypeId: BimActKc2GroupTypeEnum | undefined | null;
    onOpenOtherActKc2Details: (act: IActKc2OtherItem) => void;
    onOpenActsSmrDetail: (data: Partial<BimActKc2ListItemData>) => void;
}) => {
    const [groupEditId, setGroupEditId] = useState<number | null>(null);

    const handlerOpenEditGroup = useCallback((id: number) => {
        setGroupEditId(id);
    }, []);

    const handlerCloseEditGroup = useCallback(() => {
        setGroupEditId(null);
    }, []);

    const {
        actKc2List,
        groupState,
        changeGroupState,
        totalItemsCount,
        fetchedDataCount,
        refreshActListData,
        changeFilterHandler,
        changeOrderHandler,
    } = useGetBimActsKc2List({
        workOrderId,
        groupType: activeGroupTypeId ?? null,
    });

    const { isDeleteOpened, confirmDeletion, cancelDeletion, handleDeleteGroup } = useCommonActsKC2GroupDelete({
        refreshActListData,
        deleteQuery: bimRemoveWorkOrderActsKC2GroupGql,
    });

    const tableColumns = useBimActsKc2ListColumns({
        workOrderId,
        groupState,
        changeGroupState,
        handleDeleteGroup,
        handlerOpenEditGroup,
        changeFilterHandler,
        onOpenOtherActKc2Details,
        onOpenActsSmrDetail,
    });

    const { tableSettingsConfirmHandleCreator, isColumnVisible, pinnedItems } = useTableHideAndPinSettings({
        tableID: 'bimActKc2List',
    });

    const tableSettingsColumns = useMemo(
        () =>
            tableColumns.reduce((acc: ColDef<any>[], column, idx, fullArr) => {
                if (idx > 0) {
                    const newColumn = { ...column } as ColDef & { disabled: boolean; isPinDefDisabled: boolean };

                    if (idx < 3) {
                        newColumn.disabled = true;
                    }

                    if (idx === fullArr.length - 1) {
                        newColumn.disabled = true;
                        newColumn.isPinDefDisabled = true;
                    }

                    if (pinnedItems[getColumnId(column)]) {
                        newColumn.pinned = true;
                    }

                    acc.push(newColumn);
                }

                return acc;
            }, []),
        [pinnedItems, tableColumns],
    );

    const handleTableSettingsConfirm = useMemo(
        () => tableSettingsConfirmHandleCreator(tableSettingsColumns),
        [tableSettingsColumns, tableSettingsConfirmHandleCreator],
    );

    const noRowsTemplate = useMemo(() => {
        if (activeGroupTypeId === BimActKc2GroupTypeEnum.Custom && !actKc2List.length) {
            return 'Для отображения данных создайте группу';
        }

        return 'Нет данных';
    }, [actKc2List.length, activeGroupTypeId]);

    const fullSetColumns = useMemo(() => {
        const fullSetColumnsWithPins = getColumnsWithPinnedProp({ columns: tableColumns, pinnedItems });

        return fullSetColumnsWithPins.filter((column) => isColumnVisible(getColumnId(column)));
    }, [isColumnVisible, pinnedItems, tableColumns]);

    return {
        actKc2List,
        tableColumns: fullSetColumns,
        noRowsTemplate,
        totalItemsCount,
        fetchedDataCount,
        refreshActListData,
        isDeleteOpened,
        confirmDeletion,
        cancelDeletion,
        groupEditId,
        handlerCloseEditGroup,
        changeOrderHandler,
        tableSettingsColumns,
        handleTableSettingsConfirm,
        isColumnVisible,
    };
};
